import { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BookOpen,
  Home,
  Search,
  Users,
  UserCircle,
  Info,
  Mail,
  HelpCircle,
  LogIn,
  UserPlus,
  LayoutDashboard,
  Bookmark,
  Clock,
  Bell,
  Settings,
  Shield,
  FileText,
  Tags,
  Building2,
  MessageSquare,
  BarChart3,
  ScrollText,
  ArrowRight,
  Loader2,
} from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useAuth } from "@/contexts/AuthContext";
import { getTheses } from "@/api/theses";
import { getThesisTitle } from "@/types/thesis";
import type { ThesisListItem } from "@/types/thesis";

interface CommandItem {
  id: string;
  labelKey: string;
  icon: React.ElementType;
  path: string;
  /** Only show this item when the user is authenticated. */
  authRequired?: boolean;
  /** Only show this item when the user is NOT authenticated. */
  guestOnly?: boolean;
  /** Only show this item for admin / super_admin roles. */
  adminOnly?: boolean;
  /** Group key for categorising the item. */
  group: "navigation" | "auth" | "user" | "admin" | "actions";
}

const commandItems: CommandItem[] = [
  // ── Navigation ──────────────────────────────────────────────────────────
  {
    id: "home",
    labelKey: "nav.home",
    icon: Home,
    path: "/",
    group: "navigation",
  },
  {
    id: "catalog",
    labelKey: "nav.catalog",
    icon: BookOpen,
    path: "/catalog",
    group: "navigation",
  },
  {
    id: "authors",
    labelKey: "nav.authors",
    icon: Users,
    path: "/authors",
    group: "navigation",
  },
  {
    id: "about",
    labelKey: "nav.about",
    icon: Info,
    path: "/about",
    group: "navigation",
  },
  {
    id: "contact",
    labelKey: "nav.contact",
    icon: Mail,
    path: "/contact",
    group: "navigation",
  },
  {
    id: "faq",
    labelKey: "nav.faq",
    icon: HelpCircle,
    path: "/faq",
    group: "navigation",
  },

  // ── Auth (guest only) ───────────────────────────────────────────────────
  {
    id: "login",
    labelKey: "nav.login",
    icon: LogIn,
    path: "/login",
    guestOnly: true,
    group: "auth",
  },
  {
    id: "register",
    labelKey: "nav.register",
    icon: UserPlus,
    path: "/register",
    guestOnly: true,
    group: "auth",
  },

  // ── User (authenticated) ───────────────────────────────────────────────
  {
    id: "dashboard",
    labelKey: "nav.dashboard",
    icon: LayoutDashboard,
    path: "/dashboard",
    authRequired: true,
    group: "user",
  },
  {
    id: "bookmarks",
    labelKey: "nav.bookmarks",
    icon: Bookmark,
    path: "/bookmarks",
    authRequired: true,
    group: "user",
  },
  {
    id: "history",
    labelKey: "nav.history",
    icon: Clock,
    path: "/history",
    authRequired: true,
    group: "user",
  },
  {
    id: "notifications",
    labelKey: "nav.notifications",
    icon: Bell,
    path: "/notifications",
    authRequired: true,
    group: "user",
  },
  {
    id: "profile",
    labelKey: "nav.profile",
    icon: UserCircle,
    path: "/profile",
    authRequired: true,
    group: "user",
  },
  {
    id: "preferences",
    labelKey: "nav.preferences",
    icon: Settings,
    path: "/profile/preferences",
    authRequired: true,
    group: "user",
  },

  // ── Admin ──────────────────────────────────────────────────────────────
  {
    id: "admin-dashboard",
    labelKey: "admin.dashboard",
    icon: Shield,
    path: "/admin",
    adminOnly: true,
    group: "admin",
  },
  {
    id: "admin-theses",
    labelKey: "admin.manage_theses",
    icon: FileText,
    path: "/admin/theses",
    adminOnly: true,
    group: "admin",
  },
  {
    id: "admin-authors",
    labelKey: "admin.manage_authors",
    icon: UserCircle,
    path: "/admin/authors",
    adminOnly: true,
    group: "admin",
  },
  {
    id: "admin-users",
    labelKey: "admin.manage_users",
    icon: Users,
    path: "/admin/users",
    adminOnly: true,
    group: "admin",
  },
  {
    id: "admin-departments",
    labelKey: "admin.manage_departments",
    icon: Building2,
    path: "/admin/departments",
    adminOnly: true,
    group: "admin",
  },
  {
    id: "admin-categories",
    labelKey: "admin.manage_categories",
    icon: Tags,
    path: "/admin/categories",
    adminOnly: true,
    group: "admin",
  },
  {
    id: "admin-reviews",
    labelKey: "admin.reviews",
    icon: MessageSquare,
    path: "/admin/reviews",
    adminOnly: true,
    group: "admin",
  },
  {
    id: "admin-reports",
    labelKey: "admin.reports",
    icon: BarChart3,
    path: "/admin/reports",
    adminOnly: true,
    group: "admin",
  },
  {
    id: "admin-audit",
    labelKey: "admin.audit_log",
    icon: ScrollText,
    path: "/admin/audit-log",
    adminOnly: true,
    group: "admin",
  },
  {
    id: "admin-settings",
    labelKey: "admin.settings",
    icon: Settings,
    path: "/admin/settings",
    adminOnly: true,
    group: "admin",
  },
];

const groupLabels: Record<string, string> = {
  navigation: "command_palette.pages",
  auth: "command_palette.actions",
  user: "command_palette.pages",
  admin: "admin.dashboard",
  actions: "command_palette.actions",
};

/**
 * Global command palette accessible via `Ctrl+K` / `Cmd+K`.
 *
 * Uses shadcn's `Command` component (built on cmdk) to provide quick
 * keyboard-driven navigation across all pages and actions in the app.
 *
 * The palette filters items based on the user's authentication state and
 * role so that admin-only items are only visible to admins, guest-only items
 * are hidden when logged in, etc.
 *
 * Includes real-time thesis search: type to search theses from the API,
 * display top 5 results, and a "View all results" link to the catalog.
 *
 * Usage — mount once at the top level of the app:
 * ```tsx
 * <CommandPalette />
 * ```
 */
export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const lang = i18n.language || "fr";

  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  // ── Thesis search state ──────────────────────────────────────────────

  const [searchQuery, setSearchQuery] = useState("");
  const [thesisResults, setThesisResults] = useState<ThesisListItem[]>([]);
  const [isSearchingTheses, setIsSearchingTheses] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // ── Keyboard shortcut: Ctrl+K / Cmd+K ────────────────────────────────

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }

      // Also allow Escape to close (cmdk handles this too, belt-and-suspenders)
      if (e.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Close the palette on route change and reset search
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Reset search state when palette opens/closes
  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setThesisResults([]);
      setIsSearchingTheses(false);
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    }
  }, [open]);

  // ── Debounced thesis search ──────────────────────────────────────────

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (abortControllerRef.current) abortControllerRef.current.abort();

    const trimmed = searchQuery.trim();
    if (trimmed.length < 2) {
      setThesisResults([]);
      setIsSearchingTheses(false);
      return;
    }

    setIsSearchingTheses(true);

    searchTimerRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const result = await getTheses({
          q: trimmed,
          per_page: 5,
          page: 1,
          status: "approved",
        });

        if (!controller.signal.aborted) {
          setThesisResults(result.data);
          setIsSearchingTheses(false);
        }
      } catch {
        if (!controller.signal.aborted) {
          setThesisResults([]);
          setIsSearchingTheses(false);
        }
      }
    }, 300);

    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchQuery]);

  // ── Filter items based on auth state ──────────────────────────────────

  const filteredItems = commandItems.filter((item) => {
    if (item.authRequired && !isAuthenticated) return false;
    if (item.guestOnly && isAuthenticated) return false;
    if (item.adminOnly && !isAdmin) return false;
    return true;
  });

  // Group the filtered items
  const groups = filteredItems.reduce<Record<string, typeof commandItems>>(
    (acc, item) => {
      if (!acc[item.group]) {
        acc[item.group] = [];
      }
      acc[item.group].push(item);
      return acc;
    },
    {},
  );

  // ── Handle item selection ─────────────────────────────────────────────

  const handleSelect = useCallback(
    (path: string) => {
      // Prepend language prefix
      const fullPath = `/${lang}${path}`;
      navigate(fullPath);
      setOpen(false);
    },
    [navigate, lang],
  );

  // ── Determine ordered group keys ──────────────────────────────────────

  const groupOrder = ["navigation", "user", "auth", "admin", "actions"];
  const orderedGroupKeys = groupOrder.filter((key) => groups[key]?.length);

  // ── Handle thesis result selection ─────────────────────────────────────

  const handleThesisSelect = useCallback(
    (thesisId: string) => {
      navigate(`/${lang}/catalog/${thesisId}`);
      setOpen(false);
    },
    [navigate, lang],
  );

  const handleViewAllResults = useCallback(() => {
    const trimmed = searchQuery.trim();
    if (trimmed) {
      navigate(`/${lang}/catalog?q=${encodeURIComponent(trimmed)}`);
    } else {
      navigate(`/${lang}/catalog`);
    }
    setOpen(false);
  }, [navigate, lang, searchQuery]);

  const hasThesisResults = thesisResults.length > 0;
  const showThesisSection =
    searchQuery.trim().length >= 2 && (hasThesisResults || isSearchingTheses);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder={t("command_palette.placeholder")}
        onValueChange={(value) => setSearchQuery(value)}
      />
      <CommandList>
        <CommandEmpty>
          {isSearchingTheses
            ? t("command_palette.searching")
            : t("command_palette.no_results")}
        </CommandEmpty>

        {/* ── Thesis Search Results ──────────────────────────────────── */}
        {showThesisSection && (
          <>
            <CommandGroup
              heading={
                isSearchingTheses
                  ? t("command_palette.searching_theses")
                  : t("command_palette.thesis_results")
              }
            >
              {isSearchingTheses && !hasThesisResults && (
                <div className="flex items-center gap-2 px-2 py-3 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>{t("command_palette.searching")}</span>
                </div>
              )}

              {thesisResults.map((thesis) => {
                const { title } = getThesisTitle(thesis, lang);
                return (
                  <CommandItem
                    key={`thesis-${thesis.id}`}
                    value={`thesis ${thesis.id} ${title} ${thesis.author_name}`}
                    onSelect={() => handleThesisSelect(thesis.id)}
                    className="cursor-pointer"
                  >
                    <BookOpen className="mr-2 h-4 w-4 shrink-0 text-primary" />
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-sm">{title}</span>
                      <span className="truncate text-xs text-muted-foreground">
                        {thesis.author_name}
                        {thesis.department_name
                          ? ` · ${thesis.department_name}`
                          : ""}
                      </span>
                    </div>
                  </CommandItem>
                );
              })}

              {hasThesisResults && (
                <CommandItem
                  value="view-all-thesis-results"
                  onSelect={handleViewAllResults}
                  className="cursor-pointer"
                >
                  <ArrowRight className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="text-sm text-primary">
                    {t("command_palette.view_all_results")}
                  </span>
                </CommandItem>
              )}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {/* ── Navigation & Action Groups ─────────────────────────────── */}
        {orderedGroupKeys.map((groupKey, groupIndex) => {
          const items = groups[groupKey];
          if (!items || items.length === 0) return null;

          const groupLabel = groupLabels[groupKey] ?? "command_palette.actions";

          return (
            <div key={groupKey}>
              {groupIndex > 0 && <CommandSeparator />}
              <CommandGroup heading={t(groupLabel)}>
                {items.map((item) => {
                  const Icon = item.icon;

                  return (
                    <CommandItem
                      key={item.id}
                      value={`${item.id} ${t(item.labelKey)}`}
                      onSelect={() => handleSelect(item.path)}
                      className="cursor-pointer"
                    >
                      <Icon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                      <span>{t(item.labelKey)}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </div>
          );
        })}
      </CommandList>
    </CommandDialog>
  );
}

/**
 * Small hint badge that can be placed in the header to indicate the Ctrl+K
 * shortcut.  Clicking it also opens the palette.
 *
 * @example
 * ```tsx
 * <CommandPaletteHint />
 * ```
 */
export function CommandPaletteHint({
  className,
  onClick,
}: {
  className?: string;
  onClick?: () => void;
}) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-2.5 py-1 text-xs text-muted-foreground shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${className ?? ""}`}
      aria-label={t("accessibility.open_search")}
    >
      <Search className="h-3 w-3" />
      <span className="hidden sm:inline">{t("common.search")}</span>
      <kbd className="pointer-events-none hidden select-none items-center gap-0.5 rounded border border-border bg-muted px-1 font-mono text-[10px] font-medium text-muted-foreground sm:inline-flex">
        <span className="text-[10px]">⌘</span>K
      </kbd>
    </button>
  );
}
